import React, { useState, useEffect } from "react";
import "./PolicyDrawer.css";
import { marked } from "marked";
import { IconButton } from "../common/IconButton";
import { PolicyProject } from "winslow-core";
import ActionButton from "../common/ActionButton";
import { MyTooltip } from "../common/MyTooltip";
import { Spinner } from "../common/Spinner";
import { copyMarkdownToClipboard } from "../../hooks/usePolicyStudio";
import { Copy, Download } from "lucide-react";
import { Button } from "../../ds2/components";

interface DrawerPolicyComplianceProps {
  isOpen: boolean;
  onClose: () => void;
  handleCheckCompliance: (jurisdictionContent: string) => void;
  complianceCheckResults: PolicyProject["compliance"] | undefined;
  isLoading: boolean;
  exportToPdf: (title: string, content: string) => void;
  isDirty: boolean;
  isPublic: boolean;
}

const DrawerPolicyCompliance: React.FC<DrawerPolicyComplianceProps> = ({
  isOpen,
  onClose,
  handleCheckCompliance,
  complianceCheckResults,
  isLoading,
  exportToPdf,
  isDirty,
  isPublic,
}) => {
  const [jurisdictionContent, setJurisdictionContent] = useState(
    complianceCheckResults?.jurisdictionContent || ""
  );
  const [inputDirty, setInputDirty] = useState(false);

  useEffect(() => {
    setJurisdictionContent(complianceCheckResults?.jurisdictionContent || "");
  }, [complianceCheckResults]);

  const handleCopy = async () => {
    if (complianceCheckResults) {
      copyMarkdownToClipboard(complianceCheckResults.complianceContent);
    }
  };

  return (
    <React.Fragment>
      <div
        className={`policy-drawer-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}
      />
      <div className={`policy-drawer ${isOpen ? "open" : ""}`}>
        <div className="policy-drawer-header">
          <div className="policy-drawer-header-content">
            <div className="policy-drawer-title-container">
              <h2 className="policy-drawer-title">
                {isPublic ? "Compliance" : "Check Compliance"}
              </h2>
              {!isPublic && isDirty && (
                <div className="policy-drawer-controls-row red-text">
                  The policy content has changed since you last checked the
                  compliance. Please regenerate the compliance.
                </div>
              )}
            </div>

            <div className="policy-drawer-controls">
              <div className="policy-drawer-controls-row">
                <div className="policy-drawer-close" onClick={onClose}>
                  <img
                    src="/icons/close-icon.svg"
                    alt="Close"
                    className="policy-drawer-icon"
                  />
                </div>
              </div>
            </div>
          </div>
          {!isPublic && (
            <div className="policy-drawer-subtitle">Jurisdiction</div>
          )}
        </div>
        <div className="drawer-spinner-container">
          {isLoading && <Spinner />}
        </div>
        {isPublic ? (
          <div className="policy-drawer-public-message">
            With the full version of the Winslow Policy Studio you can check the
            policy against jurisdictional compliance.
          </div>
        ) : (
          <div
            className={`policy-drawer-search ${isLoading ? "blur-content" : ""}`}>
            <div className="policy-drawer-resource-header">
              <textarea
                className="policy-drawer-resource-textarea"
                placeholder="Enter the jurisdictional compliance you would like to check..."
                value={jurisdictionContent}
                onChange={(e) => {
                  if (e.target.value !== jurisdictionContent) {
                    setInputDirty(true);
                  }
                  setJurisdictionContent(e.target.value);
                }}
                disabled={isLoading}
              />
              <div className="policy-drawer-check-compliance">
                <Button
                  label="Check Compliance"
                  onClick={() => handleCheckCompliance(jurisdictionContent)}
                  disabled={
                    !inputDirty ||
                    isLoading ||
                    jurisdictionContent.trim().length === 0
                  }
                  variant="tertiary"
                />
              </div>
            </div>
            {complianceCheckResults && (
              <div className="policy-drawer-resource-container">
                <div className="policy-drawer-controls-row">
                  <div className="policy-drawer-controls-spacer"></div>
                  <div className="policy-drawer-controls-row red-text">
                    Please double check all compliance with your jurisdictional
                    source directly.
                  </div>
                  {!isPublic && complianceCheckResults && (
                    <div className="policy-drawer-controls-row">
                      <div className="policy-drawer-controls-spacer"></div>
                      <div className="policy-drawer-icon-group">
                        <MyTooltip title="Copy">
                          <IconButton
                            key="1"
                            // iconSrc="./icons/policy-studio-copy.svg"
                            iconComponent={<Copy size={20} />}
                            border={false}
                            alt="copy"
                            onClick={() => handleCopy()}
                            disabled={!complianceCheckResults}
                          />
                        </MyTooltip>
                        <MyTooltip title="Export">
                          <IconButton
                            key="2"
                            // iconSrc="./icons/policy-studio-export.svg"
                            iconComponent={<Download size={20} />}
                            border={false}
                            alt="export"
                            onClick={() => {
                              if (complianceCheckResults?.complianceContent) {
                                exportToPdf(
                                  "compliance-doc",
                                  complianceCheckResults.complianceContent
                                );
                              }
                            }}
                            disabled={!complianceCheckResults}
                          />
                        </MyTooltip>
                      </div>
                    </div>
                  )}
                </div>
                <div className="policy-drawer-results-content">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: marked(
                        complianceCheckResults.complianceContent
                      ) as string,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default DrawerPolicyCompliance;
